import { useMemo, useEffect, useRef } from 'react'
import { useColumnVisibilityStore } from '@/stores/useColumnVisibilityStore'
import { getDefaultHiddenColumnIds } from '@/components/PlanDetail/columnConfiguration'
import type { DagColumnMeta } from '@/types/dag'

/**
 * Encapsulates all column visibility logic: default initialization based on
 * primary-attributes, and the effective hidden column set.
 * When the user has customized columns, persisted selections are used directly.
 */
export function useColumnVisibility(
  _planId: string,
  _versionId: string | undefined,
  primaryAttributes: string[],
  columns: DagColumnMeta[] | undefined
): Set<string> {
  const hiddenColumns = useColumnVisibilityStore(s => s.hiddenColumns)
  const hideAllColumns = useColumnVisibilityStore(s => s.hideAll)
  const userHasCustomized = useColumnVisibilityStore(s => s.userHasCustomized)

  const hasInitializedRef = useRef(false)

  const defaultHiddenColumnIds = useMemo(() => {
    if (!columns) return []
    return getDefaultHiddenColumnIds(columns, primaryAttributes)
  }, [columns, primaryAttributes])

  useEffect(() => {
    if (!columns) return
    if (userHasCustomized) return
    if (hasInitializedRef.current) return

    hideAllColumns(defaultHiddenColumnIds)
    hasInitializedRef.current = true
  }, [columns, defaultHiddenColumnIds, hideAllColumns, userHasCustomized])

  return useMemo(() => {
    if (userHasCustomized) return hiddenColumns
    if (!hasInitializedRef.current) return new Set(defaultHiddenColumnIds)
    return hiddenColumns
  }, [defaultHiddenColumnIds, hiddenColumns, userHasCustomized])
}
